import { RefObject, useEffect, useRef } from 'react'
import { useMediaQuery } from 'src/utils/useMediaQuery'

/**
 * The default scroll threshold in pixels at which the CTA popup will be dismissed when the user drags it downward.
 */
const DEFAULT_DISMISS_THRESHOLD_PX = 100

/**
 * Hook that enables drag-to-dismiss on mobile viewports. When the user drags a
 * non-interactive area of the referenced container downward past a threshold and
 * releases, `onDismiss` is called. Dragging upward past the original position is
 * clamped. Horizontal movement is ignored.
 */
export const useDragToDismiss = (
  containerRef: RefObject<HTMLDivElement | null>,
  dismissThresholdPx: number = DEFAULT_DISMISS_THRESHOLD_PX,
  onDismiss: () => void = () => {}
) => {
  const isDesktop = useMediaQuery('(min-width: 48em)')
  const onDismissRef = useRef(onDismiss)

  useEffect(() => {
    onDismissRef.current = onDismiss
  }, [onDismiss])

  useEffect(() => {
    const container = containerRef.current

    if (!container) {
      return () => {}
    }

    let startY = 0
    let offsetY = 0
    let isDragging = false

    /**
     * Return whether the event target is an interactive element (like a button, link, or form control).
     *
     * @param target The event target to check.
     */
    const isInteractive = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) {
        return false
      }

      return !!target.closest(
        'button, a, [role="button"], input, select, textarea'
      )
    }

    /**
     * Handle the start of the drag gesture. If the pointer is on a non-interactive element, record the starting Y
     * position and set up for dragging. Otherwise, ignore the gesture.
     */
    const onPointerDown = (event: PointerEvent) => {
      if (isDesktop || isInteractive(event.target)) return
      startY = event.clientY
      offsetY = 0
      isDragging = true
      container.setPointerCapture(event.pointerId)
      container.style.transition = 'none'
    }

    /**
     * Update the container's vertical position as the user drags downward, clamping to the original position if they
     * drag upward above it.
     */
    const onPointerMove = (event: PointerEvent) => {
      if (!isDragging) {
        return
      }

      offsetY = Math.max(0, event.clientY - startY)
      container.style.transform = `translateY(${offsetY}px)`
    }

    /**
     * Handle the end of the drag gesture. If the user has dragged downward past the dismiss threshold, animate the
     * container off-screen and call `onDismiss`. Otherwise, animate it back to its original position.
     */
    const onPointerUp = (event: PointerEvent) => {
      if (!isDragging) {
        return
      }

      isDragging = false

      // If the pointer is still captured, release it to avoid potential issues with future pointer events.
      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId)
      }

      if (offsetY >= dismissThresholdPx) {
        // Handle dismiss by animating the container off-screen and then calling the onDismiss callback.
        container.style.transition = 'transform 200ms ease-out'
        container.style.transform = 'translateY(100%)'
        const onEnd = () => {
          container.removeEventListener('transitionend', onEnd)
          onDismissRef.current()
        }
        container.addEventListener('transitionend', onEnd)
      } else {
        // Handle cancel by animating the container back to its original position.
        container.style.transition = 'transform 200ms ease-out'
        container.style.transform = 'translateY(0)'
        const onEnd = () => {
          container.removeEventListener('transitionend', onEnd)
          container.style.transition = ''
          container.style.transform = ''
        }
        container.addEventListener('transitionend', onEnd)
      }
    }

    /**
     * Prevent the default touchmove behavior (like scrolling) when dragging, but only if the user is actively
     * dragging.
     *
     * This is necessary because on mobile devices, touchmove events can trigger scrolling, which would interfere with
     * the drag-to-dismiss gesture.
     */
    const onTouchMove = (event: TouchEvent) => {
      if (isDragging) {
        event.preventDefault()
      }
    }

    container.addEventListener('pointerdown', onPointerDown)
    container.addEventListener('pointermove', onPointerMove)
    container.addEventListener('pointerup', onPointerUp)
    container.addEventListener('pointercancel', onPointerUp)
    container.addEventListener('touchmove', onTouchMove, { passive: false })

    return () => {
      container.removeEventListener('pointerdown', onPointerDown)
      container.removeEventListener('pointermove', onPointerMove)
      container.removeEventListener('pointerup', onPointerUp)
      container.removeEventListener('pointercancel', onPointerUp)
      container.removeEventListener('touchmove', onTouchMove)

      // Reset the container's styles to their original state when the component unmounts or the effect cleans up.
      container.style.transition = ''
      container.style.transform = ''
    }
  }, [containerRef, dismissThresholdPx, isDesktop])
}
